import { type AutogiroFields } from '@resurs-internal/deposits-app-components';

import { validations } from '@/assets';
import { validationError, validationMessage } from '@/helpers';
import { lookupBankName, validateBBAN } from '@/services';
import {
  type AutogiroSettings,
  type HALLink,
} from '@/types/consumer-deposit-application-service';

import { type InitialApplication } from './useApplication';

type InitialAutogiro = {
  autogiroTerm?: HALLink;
} & InitialApplication;

export const autogiro = ({
  application,
  autogiroTerm,
  country,
  save,
}: InitialAutogiro): AutogiroFields => {
  const initialAddAutogiro: boolean | undefined =
    application?.autogiroSettings === undefined
      ? undefined
      : application.autogiroSettings?.withdrawalAmount?.amount &&
          application.autogiroSettings?.withdrawalDate
        ? true
        : false;

  const initialAccountPrefix =
    application.autogiroSettings?.accountPrefix ||
    application.disbursementAccount?.accountPrefix ||
    '';
  const initialAccountNumber =
    application.autogiroSettings?.accountNumber ||
    application.disbursementAccount?.accountNumber ||
    '';
  const initialWithdrawalAmount =
    application.autogiroSettings?.withdrawalAmount?.amount || undefined;
  const initialWithdrawalDate =
    application.autogiroSettings?.withdrawalDate || undefined;

  const interestType = application.interestType || undefined;

  const saveAutogiro = async (
    autogiroSettings: AutogiroSettings | undefined,
  ) => {
    return save({ autogiroSettings });
  };

  const validateAddAutogiro = (addAutogiro: boolean | undefined): string =>
    addAutogiro === undefined ? 'component.empty' : '';

  const validateWithdrawalAmount = (withdrawalAmount: number | undefined) =>
    validationMessage(
      true,
      validations[country].withdrawalAmount,
      validationError(
        validations[country].withdrawalAmount,
        withdrawalAmount?.toString() ?? '',
      ),
    );

  const validateWithdrawalDate = (withdrawalDate: number | undefined) =>
    validationMessage(
      true,
      validations[country].withdrawalDate,
      validationError(
        validations[country].withdrawalDate,
        withdrawalDate?.toString() ?? '',
      ),
    );

  const validateAccountNumber = async (
    accountNumber: string,
    accountPrefix: string,
  ) => {
    const errorMessage = validationMessage(
      true,
      validations[country].accountNumber,
      validationError(validations[country].accountNumber, accountNumber),
    );

    if (errorMessage) return errorMessage;

    try {
      await validateBBAN(country, accountPrefix, accountNumber);
    } catch {
      return 'category.disbursement-account.account-number.validation-error.invalid';
    }

    return '';
  };

  const validateAccountPrefix = async (accountPrefix: string) => {
    const errorMessage = validationMessage(
      true,
      validations[country].accountPrefix,
      validationError(validations[country].accountPrefix, accountPrefix),
    );

    if (errorMessage) return errorMessage;

    try {
      await lookupBankName(country, accountPrefix);
    } catch {
      return 'category.disbursement-account.clearing-number.validation-error.invalid';
    }

    return '';
  };

  return {
    autogiroTerm,
    initialAccountNumber,
    initialAccountPrefix,
    initialAddAutogiro,
    initialWithdrawalAmount,
    initialWithdrawalDate,
    interestType,
    saveAutogiro,
    validateAccountNumber,
    validateAccountPrefix,
    validateAddAutogiro,
    validateWithdrawalAmount,
    validateWithdrawalDate,
  };
};
